<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { 
	IonPage, 
	IonHeader, 
	IonToolbar, 
	IonTitle, 
	IonContent, 
	IonButtons, 
	IonBackButton,
	IonButton,
	IonSpinner,
	IonIcon
} from '@ionic/vue';
import { alertCircleOutline, chevronBackOutline, newspaperOutline } from 'ionicons/icons';
import { DateTime } from 'luxon';

import TopBar from '@/shared/components/TopBar.vue';
import NavigationBar from '@/shared/components/ui/NavigationBar.vue';
import { useSharedStore } from '@/stores/shared/sharedStore';
import useFavorite from '@/shared/composables/useFavorite';
import FeedItem from '@/shared/components/feed/FeedItem.vue';
import { DynamicPage, FeedItem as FeedItemType } from '@/shared/interfaces/dynamicPage';
import { useI18n } from 'vue-i18n';

// Route and router
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// Get page ID from route params
const pageId = computed(() => {
	const id = route.params.id;
	return typeof id === 'string' ? parseInt(id, 10) : null;
});

// Use shared store for pages data
const sharedStore = useSharedStore();

// Use favorite composable
const { toggleFavorite } = useFavorite();

// Computed properties
const page = computed<DynamicPage | undefined>(() => {
	if (!sharedStore.pages || !pageId.value) return undefined;
	return sharedStore.pages.find(p => p.id === pageId.value && p.type === 'feed');
});

const isLoading = computed(() => !sharedStore.pages || sharedStore.pages.length === 0);
const error = computed(() => !page.value && sharedStore.pages.length > 0);

const pageTitle = computed(() => {
	if (isLoading.value) return t('pages.loading');
	if (error.value) return t('pages.error');
	if (page.value) return page.value.title;
	return t('pages.not_found');
});

const feedItems = computed<FeedItemType[]>(() => {
	if (!page.value || page.value.type !== 'feed') return [];

	// Handle both string and array content types
	if (typeof page.value.content === 'string') {
		try {
			return JSON.parse(page.value.content);
		} catch {
			return [];
		}
	}

	return Array.isArray(page.value.content) ? page.value.content : [];
});

const showNavigationBar = computed(() => {
	return page.value?.display_mode === 'content';
});

// Methods
const goBack = () => {
	router.push('/dashboard');
};

const formatDate = (dateString: string): string => {
	try {
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	} catch {
		return dateString;
	}
};

const formatTime = (dateTimeString: string): string => {
	if (!dateTimeString) return '';
	
	try {
		return DateTime.fromFormat(dateTimeString, 'yyyy-MM-dd HH:mm:ss').toFormat('HH:mm');
	} catch {
		return '';
	}
};
</script>

<template>
	<ion-content>
		<TopBar class="px-[34px]">
			<template #left>
				<div class="flex items-center">
					<ion-button class="w-[30px] h-[30px] text-black ion-no-padding ion-no-margin hover:bg-white hover:rounded-full" shape="round" @click="goBack">
						<ion-icon :icon="chevronBackOutline" class="ion-no-padding ion-no-margin text-black mr-[2px]"></ion-icon>
					</ion-button>
				</div>
			</template>

			<template #center>
				<div class="flex grow justify-center w-full font-sourcesanspro-semibold text-l2">
				{{ pageTitle }}
				</div>
			</template>

			<template #right>

			</template>
		</TopBar>

		
		<div v-if="isLoading" class="flex justify-center items-center h-full">
			<ion-spinner name="crescent"></ion-spinner>
		</div>

		<div v-else-if="error" class="flex flex-col justify-center items-center h-full p-4">
			<div class="bg-white border rounded-lg p-8 flex flex-col items-center">
				<h2 class="text-xl font-semibold text-gray-800 mb-2">{{ $t('pages.not_found') }}</h2>
				<p class="text-gray-600 text-center">
					{{ $t('pages.not_found_description') }}
				</p>
			</div>
		</div>

		<div v-else-if="page && feedItems.length > 0" class="p-[34px]">
			<FeedItem 
				v-for="(item, idx) in feedItems"
				:return-to="`/pages/feed/${pageId}`"
				:key="idx"
				:going="item.going || false"
				:id="item.id"
				:canceled-at="item.canceled_at"
				:favorite="item.is_favorite || false"
				:summary="item.summary || ''"
				:image="item.main_image_url || ''"
				:link="`${item.type && item.type.includes('event') ? '/events' : '/news'}/${item.id}`"
				:type="item.type"
				:location-address="item.address || ''"
				:happening-now="item.happening_now || false"
				:happening-soon="item.happening_soon || false"
				:is-event="item.type === 'event'"
				:date="item.start_datetime || ''"
				:date-end="item.end_datetime || ''"
				:is-pinned="item.is_pinned || false"
				:hour="formatTime(item.start_datetime || '')"
				:hour-end="formatTime(item.end_datetime || '')"
				:show-logged-data="true"
				:updated-at="item.updated_at || ''"
				:title="item.title || item.name || ''"
				@on-favorite-clicked="toggleFavorite" />
		</div>

		<div v-else-if="page && feedItems.length === 0" class="flex flex-col justify-center items-center h-full p-4">
			<div class="bg-white rounded-lg p-8 flex flex-col items-center w-full max-w-md">
				<h2 class="text-xl font-semibold text-gray-800 mb-2">{{ $t('pages.no_content_available') }}</h2>
				<p class="text-gray-600 text-center">
					{{ $t('pages.no_content_available_description') }}
				</p>
			</div>
		</div>

		<!-- Navigation Bar (only for 'content' display mode) -->
		<NavigationBar v-if="showNavigationBar" item-active="pages" />
	</ion-content>
</template>

<style scoped>
</style> 